import { ActionSheetButton, IonActionSheet } from '@ionic/react';
import { useField } from 'formik';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import {
  ScratchpadBucket,
  ScratchpadBucketType,
} from '../../../../../../../api/graphql/generated';
import { IonFormExternalPicker } from '../../../../../../../components/form/ion-form-external-picker';
import i18n from '../../../../../../../i18n';
import { ApplicationSettingsSearch } from '../../../../../shared/component/settings/application-settings-search';
import { useFlowServices } from '../../../../../shared/hook/flow-service.hooks';

interface MultipassScheduleWeldLibraryInputProps {
  name: string;
  label?: string;
  disabled?: boolean;
  presentingEl?: HTMLElement;
  isFullScreen?: boolean;
}

const MultipassScheduleWeldLibraryInput = (
  props: MultipassScheduleWeldLibraryInputProps
): ReactElement => {
  const { name, disabled, presentingEl, isFullScreen, label } = props;

  const [showBucketExplorer, setShowBucketExplorer] = useState(false);
  const [showLibraryActions, setShowLibraryActions] = useState(false);

  const [field, _meta, helper] = useField<ScratchpadBucket | undefined>(name);
  const { value: bucket } = field;

  const { flowService, system } = useFlowServices();
  const getBucketSubheading = useCallback(
    (bucket: ScratchpadBucket) => {
      return flowService.getApplicationDefaultParameterSetSubheadingFromBucket(
        bucket
      );
    },
    [flowService]
  );

  const actionButtons = useMemo(() => {
    const buttons: ActionSheetButton[] = [
      {
        text: i18n.t('common:select_from_library'),
        handler: () => {
          setShowBucketExplorer(true);
        },
      },
    ];

    if (field.value) {
      buttons.push({
        text: i18n.t('common:remove_library'),
        role: 'destructive',
        handler: () => {
          helper.setValue(undefined);
        },
      });
    }

    buttons.push({
      text: i18n.t('common:cancel'),
      role: 'cancel',
    });

    return buttons;
  }, [field.value, helper]);

  const bucketName = useMemo(() => {
    return bucket?.name ?? undefined;
  }, [bucket?.name]);

  const onPickerClick = useCallback(() => {
    if (bucket) {
      setShowLibraryActions(true);
      return;
    }
    setShowBucketExplorer(true);
  }, [bucket]);

  return (
    <>
      <IonFormExternalPicker
        selectedName={bucketName}
        placeholder={i18n.t('common:library')}
        disabled={disabled}
        onClick={onPickerClick}
        onUnset={() => {
          helper.setValue(undefined);
        }}
        ionItemProps={{
          lines: 'none',
        }}
      />
      <IonActionSheet
        isOpen={showLibraryActions}
        header={label}
        onDidDismiss={() => {
          setShowLibraryActions(false);
        }}
        buttons={actionButtons}
      />
      <ApplicationSettingsSearch
        isOpen={showBucketExplorer}
        isFullScreen={isFullScreen}
        presentingElement={presentingEl}
        onClose={() => {
          setShowBucketExplorer(false);
        }}
        onSelected={async (bucket) => {
          helper.setValue(bucket);
          setShowBucketExplorer(false);
        }}
        system={system}
        showFooter={false}
        implicitSearchTags={{
          welder: flowService.getEquipmentName(),
        }}
        getBucketSubheading={getBucketSubheading}
        type={ScratchpadBucketType.WELD_SETTINGS}
      />
    </>
  );
};

export { MultipassScheduleWeldLibraryInput };
